import styled from '@emotion/styled-base';
import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { HeadingText30, Input, InputType, SelectField } from '@modernhealth/vital-components';
import { useSelector } from 'react-redux';

import { Button } from 'src/shared/components/Button';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { sexIdentityOptions } from '../models/sexIdentityOptions';
import { usaStatesOptions, getMatchingUSAState } from '../models/statesOptions';
import { isPolicyHolder, addDashToZip } from '../helpers/insuranceHelpers';

export interface InsuranceRelationshipOption {
    code: string;
    description: string;
}
export interface InsuranceMemberData {
    firstName?: string;
    lastName?: string;
    sex?: string;
    birthDate?: string;
    addressLine1?: string;
    addressLine2?: string;
    addressCity?: string;
    addressState?: string;
    addressZip?: string;
    relationshipCode?: string;
}

export interface InsurancePlanData {
    subscriberId: string;
    policyNumber: string;
    primaryFirstName?: string;
    primaryLastName?: string;
}

export enum EnterInsuranceInfoFormTestID {
    Wrapper = 'EnterInsuranceInfoFormTestID.Wrapper',
    SubscriberId = 'EnterInsuranceInfoFormTestId.SubscriberId',
    PolicyNumber = 'EnterInsuranceInfoFormTestId.PolicyNumber',
    FirstName = 'EnterInsuranceInfoFormTestId.FirstName',
    PrimaryFirstName = 'EnterInsuranceInfoFormTestId.PrimaryFirstName',
    LastName = 'EnterInsuranceInfoFormTestId.LastName',
    PrimaryLastName = 'EnterInsuranceInfoFormTestId.PrimaryLastName',
    Sex = 'EnterInsuranceInfoFormTestId.Sex',
    RelationshipCode = 'EnterInsuranceInfoFormTestId.RelationshipCode',
    City = 'EnterInsuranceInfoFormTestId.City',
    Zip = 'EnterInsuranceInfoFormTestId.Zip',
    SubmitButton = 'EnterInsuranceInfoFormTestID.SubmitButton',
}

export interface Props {
    onCancel: string | (() => void);
    onFormSubmit: (formData: InsuranceMemberData & InsurancePlanData) => void;
    relationshipOptions: InsuranceRelationshipOption[];
    isLoading: boolean;
}

const SUBSCRIBER_ID_MIN_LENGTH = 2;
const SUBSCRIBER_ID_MAX_LENGTH = 80;
const POLICY_NUMBER_MAX_LENGTH = 50;
const FIRST_NAME_MIN_LENGTH = 1;
const FIRST_NAME_MAX_LENGTH = 35;
const LAST_NAME_MIN_LENGTH = 1;
const LAST_NAME_MAX_LENGTH = 60;
const ADDRESS_CITY_MAX_LENGTH = 60;
const ADDRESS_ZIP_REGEX = /^(\d{5})([- ]\d{4})?$/;

const FormWrapper = styled('form')({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
});

const FormRow = styled('div')({
    display: 'flex',
    gap: 16,
    alignItems: 'flex-start',
});

const ButtonsRow = styled('div')({
    display: 'flex',
    paddingTop: 24,
    gap: 18,
});

export const EnterInsuranceInfoForm = (props: Props) => {
    const userDefaultData = useSelector(getUserInformation);
    const [subscriberId, setSubscriberId] = useState<string>('');
    const [subscriberIdError, setSubscriberIdError] = useState<string>('');
    const [policyNumber, setPolicyNumber] = useState<string>('');
    const [policyNumberError, setPolicyNumberError] = useState<string>('');
    const [firstName, setFirstName] = useState<string>(userDefaultData.firstName);
    const [firstNameError, setFirstNameError] = useState<string>('');
    const [lastName, setLastName] = useState<string>(userDefaultData.lastName);
    const [lastNameError, setLastNameError] = useState<string>('');
    const [sex, setSex] = useState<string>('');
    const [birthDate, setBirthDate] = useState<string>(userDefaultData.birthDate ?? '');
    const [addressLine1, setAddressLine1] = useState<string>(userDefaultData.mailingAddress);
    const [addressLine2, setAddressLine2] = useState<string>('');
    const [addressCity, setAddressCity] = useState<string>('');
    const [addressCityError, setAddressCityError] = useState<string>('');
    const [addressState, setAddressState] = useState<string>(
        getMatchingUSAState(userDefaultData.state)?.stateCode ?? '',
    );
    const [addressZip, setAddressZip] = useState<string>(userDefaultData.zipCode ?? '');
    const [addressZipError, setAddressZipError] = useState<string>('');
    const [relationshipCode, setRelationshipCode] = useState<string>('');
    const [primaryFirstName, setPrimaryFirstName] = useState<string>('');
    const [primaryFirstNameError, setPrimaryFirstNameError] = useState<string>('');
    const [primaryLastName, setPrimaryLastName] = useState<string>('');
    const [primaryLastNameError, setPrimaryLastNameError] = useState<string>('');
    const [submitBlocked, setSubmitBlocked] = useState<boolean>(true);
    const { t } = useTranslation();

    const isRelationshipCodeSelected = !!relationshipCode;
    const baseErrorFields = {
        subscriberIdError,
        policyNumberError,
        firstNameError,
        lastNameError,
        addressCityError,
        addressZipError,
    };
    const dependentErrorFields = { primaryFirstNameError, primaryLastNameError };
    const errorFields = isPolicyHolder(relationshipCode)
        ? baseErrorFields
        : { ...baseErrorFields, ...dependentErrorFields };

    const baseRequiredFields = {
        firstName,
        lastName,
        sex,
        birthDate,
        addressLine1,
        addressCity,
        addressState,
        addressZip,
        subscriberId,
        policyNumber,
        relationshipCode,
    };
    const dependentRequiredFields = { primaryFirstName, primaryLastName };
    const requiredFields = isPolicyHolder(relationshipCode)
        ? baseRequiredFields
        : { ...baseRequiredFields, ...dependentRequiredFields };

    const policyHolderFieldLabels = useMemo(() => {
        if (isPolicyHolder(relationshipCode)) {
            return {
                subscriberId: t('insuranceForm:insurance_form.insurance_subscriber_member_id'),
                policyNumber: t('insuranceForm:insurance_form.group_number'),
            };
        }
        return {
            subscriberId: t('insuranceForm:insurance_form.insurance_holder_subscriber_member_id'),
            policyNumber: t('insuranceForm:insurance_form.holder_group_number'),
        };
    }, [t, relationshipCode]);

    const handleSubscriberIdChange = value => {
        if (value.length > SUBSCRIBER_ID_MAX_LENGTH || value.length < SUBSCRIBER_ID_MIN_LENGTH) {
            setSubscriberIdError(t('insuranceForm:insurance_form.subscriber_id_error'));
        } else {
            setSubscriberIdError('');
        }

        setSubscriberId(value);
    };

    const handlePolicyNumberChange = value => {
        if (value.length > POLICY_NUMBER_MAX_LENGTH) {
            setPolicyNumberError(t('insuranceForm:insurance_form.policy_number_error'));
        } else {
            setPolicyNumberError('');
        }

        setPolicyNumber(value);
    };

    const handleFirstNameChange = value => {
        if (value.length === 0) {
            setFirstNameError(t('validation.required.firstName'));
        } else if (value.length > FIRST_NAME_MAX_LENGTH || value.length < FIRST_NAME_MIN_LENGTH) {
            setFirstNameError(t('insuranceForm:insurance_form.first_name_error'));
        } else {
            setFirstNameError('');
        }

        setFirstName(value);
    };
    const handleLastNameChange = value => {
        if (value.length === 0) {
            setLastNameError(t('validation.required.lastName'));
        } else if (value.length > LAST_NAME_MAX_LENGTH || value.length < LAST_NAME_MIN_LENGTH) {
            setLastNameError(t('insuranceForm:insurance_form.last_name_error'));
        } else {
            setLastNameError('');
        }

        setLastName(value);
    };
    const handleSexChange = value => setSex(value?.value);
    const handleBirthDateChange = value => setBirthDate(value);
    const handleAddressLine1Change = value => setAddressLine1(value);
    const handleAddressLine2Change = value => setAddressLine2(value);
    const handleAddressCityChange = value => {
        if (value.length > ADDRESS_CITY_MAX_LENGTH) {
            setAddressCityError(t('insuranceForm:insurance_form.address_city_error'));
        } else {
            setAddressCityError('');
        }

        setAddressCity(value);
    };
    const handleAddressStateChange = value => setAddressState(value?.value?.toLowerCase());
    const handleAddressZipChange = value => {
        value = addDashToZip(value);
        if (value.match(ADDRESS_ZIP_REGEX)) {
            setAddressZipError('');
        } else {
            setAddressZipError(t('insuranceForm:insurance_form.address_zip_error'));
        }

        setAddressZip(value);
    };
    const handleRelationshipChange = value => setRelationshipCode(value?.value);

    const handlePrimaryFirstNameChange = value => {
        if (
            !isPolicyHolder(relationshipCode) &&
            (value.length > FIRST_NAME_MAX_LENGTH || value.length < FIRST_NAME_MIN_LENGTH)
        ) {
            setPrimaryFirstNameError(t('insuranceForm:insurance_form.first_name_error'));
        } else {
            setPrimaryFirstNameError('');
        }

        setPrimaryFirstName(value);
    };
    const handlePrimaryLastNameChange = value => {
        if (
            !isPolicyHolder(relationshipCode) &&
            (value.length > LAST_NAME_MAX_LENGTH || value.length < LAST_NAME_MIN_LENGTH)
        ) {
            setPrimaryLastNameError(t('insuranceForm:insurance_form.last_name_error'));
        } else {
            setPrimaryLastNameError('');
        }

        setPrimaryLastName(value);
    };

    const handleFormSubmit = (event?) => {
        if (event) {
            event.preventDefault();
        }

        if (submitBlocked) {
            return;
        }

        props.onFormSubmit({
            firstName,
            lastName,
            sex,
            birthDate,
            addressLine1,
            addressLine2,
            addressCity,
            addressState,
            addressZip,
            subscriberId,
            policyNumber,
            relationshipCode,
            primaryFirstName,
            primaryLastName,
        });
    };

    useEffect(() => {
        const hasErrors = Object.values(errorFields).some(errorStr => errorStr);
        const hasMissingRequiredFields = Object.values(requiredFields).includes('');

        setSubmitBlocked(hasErrors || hasMissingRequiredFields);
    }, [errorFields, requiredFields]);

    return (
        <FormWrapper onSubmit={handleFormSubmit} data-testid={EnterInsuranceInfoFormTestID.Wrapper}>
            <HeadingText30>{t('insuranceForm:insurance_form.form_heading')}</HeadingText30>

            <FormRow>
                <Input
                    label={t('auth:registration_form.labels.firstName')}
                    type={InputType.TextField}
                    fieldId="firstName"
                    value={firstName}
                    testID={EnterInsuranceInfoFormTestID.FirstName}
                    onChange={handleFirstNameChange}
                    error={firstNameError}
                />
                <Input
                    label={t('auth:registration_form.labels.lastName')}
                    type={InputType.TextField}
                    fieldId="lastName"
                    value={lastName}
                    testID={EnterInsuranceInfoFormTestID.LastName}
                    onChange={handleLastNameChange}
                    error={lastNameError}
                />
            </FormRow>

            <FormRow>
                <Input
                    label={t('auth:registration_form.labels.dateOfBirth')}
                    type={InputType.DatePicker}
                    initialValue={birthDate}
                    fieldId="birthDate"
                    onChange={handleBirthDateChange}
                />
            </FormRow>

            <FormRow>
                <SelectField
                    fieldId="sex"
                    label={t('insuranceForm:insurance_form.sex')}
                    options={sexIdentityOptions.map(({ value, translationId }) => ({
                        value,
                        label: t(translationId),
                    }))}
                    value={sex}
                    data-testid={EnterInsuranceInfoFormTestID.Sex}
                    onChange={handleSexChange}
                />
            </FormRow>

            <FormRow>
                <Input
                    label={t('insuranceForm:insurance_form.address1')}
                    type={InputType.TextField}
                    value={addressLine1}
                    fieldId="addressLine1"
                    onChange={handleAddressLine1Change}
                />
                <Input
                    label={t('insuranceForm:insurance_form.address2')}
                    type={InputType.TextField}
                    value={addressLine2}
                    fieldId="addressLine2"
                    onChange={handleAddressLine2Change}
                />
            </FormRow>

            <FormRow>
                <Input
                    label={t('providers:additional_info.city')}
                    type={InputType.TextField}
                    value={addressCity}
                    testID={EnterInsuranceInfoFormTestID.City}
                    fieldId="addressCity"
                    onChange={handleAddressCityChange}
                    error={addressCityError}
                />
            </FormRow>

            <FormRow>
                <SelectField
                    fieldId="addressState"
                    label={t('providers:additional_info.state')}
                    options={usaStatesOptions.map(state => ({
                        value: state.stateCode.toLowerCase(),
                        label: state.fullName,
                    }))}
                    value={addressState.toLowerCase()}
                    onChange={handleAddressStateChange}
                />
                <Input
                    label={t('providers:additional_info.postal_code')}
                    type={InputType.TextField}
                    value={addressZip}
                    testID={EnterInsuranceInfoFormTestID.Zip}
                    fieldId="addressZip"
                    onChange={handleAddressZipChange}
                    error={addressZipError}
                />
            </FormRow>

            <HeadingText30 marginTop={8}>
                {t('insuranceForm:insurance_form.relationship.heading')}
            </HeadingText30>

            <FormRow>
                <SelectField
                    fieldId="relationshipSelect"
                    label={t('insuranceForm:insurance_form.relationship.placeholder')}
                    options={props.relationshipOptions.map(option => ({
                        value: option.code,
                        label: option.description,
                    }))}
                    value={relationshipCode}
                    data-testid={EnterInsuranceInfoFormTestID.RelationshipCode}
                    onChange={handleRelationshipChange}
                />
            </FormRow>

            {isRelationshipCodeSelected && !isPolicyHolder(relationshipCode) && (
                <FormRow>
                    <Input
                        label={t('insuranceForm:insurance_form.relationship.first_name')}
                        type={InputType.TextField}
                        fieldId="firstName"
                        value={primaryFirstName}
                        testID={EnterInsuranceInfoFormTestID.PrimaryFirstName}
                        onChange={handlePrimaryFirstNameChange}
                        error={primaryFirstNameError}
                    />
                    <Input
                        label={t('insuranceForm:insurance_form.relationship.last_name')}
                        type={InputType.TextField}
                        fieldId="lastName"
                        value={primaryLastName}
                        testID={EnterInsuranceInfoFormTestID.PrimaryLastName}
                        onChange={handlePrimaryLastNameChange}
                        error={primaryLastNameError}
                    />
                </FormRow>
            )}

            {isRelationshipCodeSelected && (
                <FormRow>
                    <Input
                        label={policyHolderFieldLabels.subscriberId}
                        type={InputType.TextField}
                        fieldId="subscriberId"
                        value={subscriberId}
                        testID={EnterInsuranceInfoFormTestID.SubscriberId}
                        onChange={handleSubscriberIdChange}
                        error={subscriberIdError}
                    />
                </FormRow>
            )}

            {isRelationshipCodeSelected && (
                <FormRow>
                    <Input
                        label={policyHolderFieldLabels.policyNumber}
                        type={InputType.TextField}
                        fieldId="policyNumber"
                        value={policyNumber}
                        testID={EnterInsuranceInfoFormTestID.PolicyNumber}
                        onChange={handlePolicyNumberChange}
                        error={policyNumberError}
                    />
                </FormRow>
            )}

            <ButtonsRow>
                {typeof props.onCancel === 'function' && (
                    <Button fullWidth type="white" onClick={props.onCancel}>
                        {t('translation:common.cancel')}
                    </Button>
                )}
                {typeof props.onCancel === 'string' && (
                    <Button fullWidth type="white" to={props.onCancel}>
                        {t('translation:common.cancel')}
                    </Button>
                )}
                <Button
                    fullWidth
                    testID={EnterInsuranceInfoFormTestID.SubmitButton}
                    disabled={submitBlocked || props.isLoading}
                    onClick={handleFormSubmit}
                >
                    {t('translation:common.submit')}
                </Button>
            </ButtonsRow>
        </FormWrapper>
    );
};
